class CustomCarousel extends HTMLElement {
	constructor() {
		super();
		this.splide = null;
		this.initialRowsToShow = 2; // Show first 2 rows initially
		this.rowsPerLoad = 2; // Load 2 more rows each time
		this.currentlyShownRows = 0;
		this.totalRows = 0;
	}

	connectedCallback() {
		const hiddenHost = this.closest('x-modal, .modal, cart-drawer, [hidden]');
		if (hiddenHost && !(window.Shopify && Shopify.designMode)) {
			this.observeHiddenHost(hiddenHost);
			return;
		}

		this.boot();
	}

	observeHiddenHost(host) {
		const bootWhenVisible = () => {
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					if (typeof this.reinit === 'function') {
						this.reinit();
					} else {
						this.boot();
					}
				});
			});
		};

		this._afterShow = () => bootWhenVisible();
		host.addEventListener('dialog:after-show', this._afterShow);

		if (host.hasAttribute('open')) {
			bootWhenVisible();
		}

		this._modalObserver = new MutationObserver(() => {
			if (host.hasAttribute('open')) {
				bootWhenVisible();
			}
		});
		this._modalObserver.observe(host, { attributes: true, attributeFilter: ['open', 'class'] });

		this._modalClick = (event) => {
			const id = host.id;
			if (id && event.target.closest && event.target.closest(`[aria-controls="${id}"]`)) {
				setTimeout(bootWhenVisible, 80);
				setTimeout(bootWhenVisible, 320);
			}
		};
		document.addEventListener('click', this._modalClick, true);
	}

	disconnectBootObservers() {
		if (this._bootObserver) {
			this._bootObserver.disconnect();
			this._bootObserver = null;
		}
		if (this._modalObserver) {
			this._modalObserver.disconnect();
			this._modalObserver = null;
		}
		if (this._modalClick) {
			document.removeEventListener('click', this._modalClick, true);
			this._modalClick = null;
		}
		if (this._unlockListener) {
			document.removeEventListener('theme:below-fold-unlock', this._unlockListener);
			this._unlockListener = null;
		}
		if (this._afterShow) {
			const host = this.closest('x-modal, .modal, cart-drawer');
			if (host) host.removeEventListener('dialog:after-show', this._afterShow);
			this._afterShow = null;
		}
	}

	reinit() {
		clearTimeout(this._reinitTimer);
		this._reinitTimer = setTimeout(() => {
			this.disconnectBootObservers();
			if (this.splide) {
				try {
					this.splide.destroy();
				} catch (error) {
					/* already destroyed */
				}
				this.splide = null;
			}
			this._booted = false;
			this.boot();
		}, 40);
	}

	boot() {
		if (this._booted) return;
		this._booted = true;
		this.disconnectBootObservers();

		this.filterScheduledItems();
		this.optimizeInitialImages();

		const enableDesktopCarousel = this.getAttribute('enable-desktop-carousel') === 'true';
		const enableMobileCarousel = this.getAttribute('enable-mobile-carousel') === 'true';

		const desktopColumns = parseInt(this.getAttribute('desktop-columns')) || 3;
		const mobileColumns = parseInt(this.getAttribute('mobile-columns')) || 2;
		const desktopGap = parseInt(this.getAttribute('desktop-gap')) || 0;
		const mobileGap = parseInt(this.getAttribute('mobile-gap')) || 0;

		const desktopAutoplay = this.getAttribute('desktop-autoplay') === 'true';
		const mobileAutoplay = this.getAttribute('mobile-autoplay') === 'true';
		const desktopInterval = parseInt(this.getAttribute('desktop-interval')) || 5000;
		const mobileInterval = parseInt(this.getAttribute('mobile-interval')) || 5000;
		const desktopInfiniteLoop = this.getAttribute('desktop-infinite-loop') !== 'false';
		const mobileInfiniteLoop = this.getAttribute('mobile-infinite-loop') !== 'false';
		const desktopDraggable = this.getAttribute('desktop-draggable') !== 'false';
		const mobileDraggable = this.getAttribute('mobile-draggable') !== 'false';
		const carouselType = this.getAttribute('carousel-type') || 'slide';

		const showArrows = this.getAttribute('show-arrows') !== 'false';
		const showDots = this.getAttribute('show-dots') !== 'false';
		const perMove = parseInt(this.getAttribute('per-move')) || 1;

		this.enableViewMore = this.getAttribute('enable-view-more') !== 'false';

		const isMobile = window.innerWidth < 750;

		if ((isMobile && enableMobileCarousel) || (!isMobile && enableDesktopCarousel)) {
			let resolvedType = carouselType;
			const infiniteEnabled = isMobile ? mobileInfiniteLoop : desktopInfiniteLoop;
			if (infiniteEnabled && carouselType !== 'fade') {
				resolvedType = 'loop';
			}

			this.initSplide({
				perPage: isMobile ? mobileColumns : desktopColumns,
				gap: `${isMobile ? mobileGap : desktopGap}px`,
				autoplay: isMobile ? mobileAutoplay : desktopAutoplay,
				interval: isMobile ? mobileInterval : desktopInterval,
				arrows: showArrows,
				pagination: showDots,
				perMove: perMove,
				type: resolvedType,
				rewind: carouselType === 'fade' ? true : undefined,
				start: 0,
				drag: isMobile ? mobileDraggable : desktopDraggable,
				speed: 400,
				easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
				waitForTransition: false,
				slideFocus: false,
				reducedMotion: {
					speed: 400,
					rewindSpeed: 400
				}
			});
		} else {
			this.classList.remove('splide');
			const track = this.querySelector('.splide__track');
			const list = this.querySelector('.splide__list');

			if (track && list) {
				track.parentNode.insertBefore(list, track);
				track.parentNode.removeChild(track);

				list.classList.remove('splide__list');
				list.querySelectorAll('.splide__slide').forEach((slide) => {
					slide.classList.remove('splide__slide');
				});

				this.setupGridView(list, isMobile ? mobileColumns : desktopColumns);
			}
		}

		if (!this._resizeBound) {
			this.handleResize = this.handleResize.bind(this);
			window.addEventListener('resize', this.handleResize, { passive: true });
			this._resizeBound = true;
		}
	}

	optimizeInitialImages() {
		// Set lazy loading for all images that aren't in the first visible slide
		const slides = this.querySelectorAll('.splide__slide');
		if (slides.length === 0) return;
		
		// First slide images can be eager, rest should be lazy
		slides.forEach((slide, index) => {
			const images = slide.querySelectorAll('img');
			images.forEach(img => {
				// Skip if already has loading attribute
				if (img.hasAttribute('loading')) return;
				
				// First slide (index 0) can be eager, others should be lazy
				if (index === 0) {
					// Check if image is above the fold
					const rect = slide.getBoundingClientRect();
					if (rect.top < window.innerHeight) {
						img.loading = 'eager';
					} else {
						img.loading = 'lazy';
					}
				} else {
					img.loading = 'lazy';
				}
			});
		});
	}
	
	setupGridView(list, columnsPerRow) {
		const items = Array.from(list.children);
		if (items.length === 0) return;

		this.list = list;
		this.columnsPerRow = columnsPerRow;
		this.totalRows = Math.ceil(items.length / columnsPerRow);
		this.currentlyShownRows = this.initialRowsToShow;

		const viewAllContainer = list.closest('.custom-tab-content')?.querySelector('.product-tabs-content-bottom');
		this.viewAllContainer = viewAllContainer;

		const existingButtons = list.parentNode.querySelector(':scope > .grid-view-buttons');

		if (!this.enableViewMore || this.totalRows <= this.initialRowsToShow) {
			this.currentlyShownRows = this.totalRows;
			this.applyViewMoreVisibility();
			existingButtons?.remove();
			if (viewAllContainer) viewAllContainer.style.display = 'flex';
			return;
		}

		this.applyViewMoreVisibility();

		if (!existingButtons) {
			const buttonContainer = document.createElement('div');
			buttonContainer.className = 'grid-view-buttons';
			list.parentNode.insertBefore(buttonContainer, list.nextSibling);
			this.createViewMoreButton(buttonContainer);
		}

		if (viewAllContainer) viewAllContainer.style.display = 'none';
	}

	applyViewMoreVisibility() {
		if (!this.list) return;
		Array.from(this.list.children).forEach((item, index) => {
			const row = Math.floor(index / this.columnsPerRow);
			item.classList.toggle('grid-item--view-more-hidden', row >= this.currentlyShownRows);
		});
	}
	
	createViewMoreButton(container) {
		if (container.querySelector('.view-more-button')) return;

		const viewMoreBtn = document.createElement('button');
		viewMoreBtn.type = 'button';
		viewMoreBtn.className = 'view-more-button';
		viewMoreBtn.textContent = 'View More';
		
		viewMoreBtn.addEventListener('click', () => {
			this.currentlyShownRows = Math.min(this.currentlyShownRows + this.rowsPerLoad, this.totalRows);
			this.applyViewMoreVisibility();

			document.dispatchEvent(new CustomEvent("swym:collections-loaded"));
			
			if (this.currentlyShownRows >= this.totalRows) {
				viewMoreBtn.remove();
				if (this.viewAllContainer) {
					this.viewAllContainer.style.display = 'flex';
				}
			}
		});
		
		container.appendChild(viewMoreBtn);
	}

	filterScheduledItems() {
		const items = this.querySelectorAll('.image-link-item');
		const now = new Date();
		
		items.forEach(item => {
			const enableSchedule = item.getAttribute('data-schedule-enabled') === 'true';
			if (!enableSchedule) return;

			const startStr = item.getAttribute('data-schedule-start');
			const endStr = item.getAttribute('data-schedule-end');

			if (!this.isItemVisible(now, startStr, endStr)) {
				item.remove();
			} else {
				item.style.display = '';
			}
		});
	}

	isItemVisible(now, startStr, endStr) {
		const parseDate = (dateStr) => {
			if (!dateStr) return null;
			const parts = dateStr.split('-');
			if (parts.length !== 3) return null;
			if (parts[0].length === 4) {
				return new Date(parts[0], parts[1] - 1, parts[2]);
			}
			return new Date(parts[2], parts[1] - 1, parts[0]);
		};

		const parseTime = (timeStr) => {
			if (!timeStr) return null;
			const parts = timeStr.split(':');
			if (parts.length < 2) return null;
			return { hours: parseInt(parts[0], 10), minutes: parseInt(parts[1], 10) };
		};

		// Parse "DD-MM-YYYY" or "DD-MM-YYYY HH:MM" and return Date
		const parseDateTime = (str, isEnd) => {
			if (!str || !str.trim()) return null;
			const trimmed = str.trim();
			const parts = trimmed.split(/\s+/);
			const dateStr = parts[0];
			const timeStr = parts[1];
			const date = parseDate(dateStr);
			if (!date) return null;
			if (timeStr) {
				const time = parseTime(timeStr);
				if (time) {
					if (isEnd) {
						date.setHours(time.hours, time.minutes, 59, 999);
					} else {
						date.setHours(time.hours, time.minutes, 0, 0);
					}
				} else {
					date.setHours(isEnd ? 23 : 0, isEnd ? 59 : 0, isEnd ? 59 : 0, isEnd ? 999 : 0);
				}
			} else {
				date.setHours(isEnd ? 23 : 0, isEnd ? 59 : 0, isEnd ? 59 : 0, isEnd ? 999 : 0);
			}
			return date;
		};

		// Check Start
		if (startStr && startStr.trim()) {
			const startDate = parseDateTime(startStr, false);
			if (startDate && now < startDate) return false;
		}

		// Check End
		if (endStr && endStr.trim()) {
			const endDate = parseDateTime(endStr, true);
			if (endDate && now > endDate) return false;
		}

		return true;
	}
	
	hydrateDeferredMedia(root = this) {
		if (!root) return;
		root.querySelectorAll('source[data-srcset]').forEach((source) => {
			source.setAttribute('srcset', source.getAttribute('data-srcset'));
			source.removeAttribute('data-srcset');
		});
		root.querySelectorAll('img[data-srcset]').forEach((img) => {
			img.setAttribute('srcset', img.getAttribute('data-srcset'));
			img.removeAttribute('data-srcset');
		});
		root.querySelectorAll('img[data-src]').forEach((img) => {
			img.setAttribute('src', img.getAttribute('data-src'));
			img.removeAttribute('data-src');
			img.classList.remove('deferred-media');
		});
	}

	lazyLoadImagesInElement(element) {
		this.hydrateDeferredMedia(element);

		const lazyBackgrounds = element.querySelectorAll('[data-bg]');
		for (const el of lazyBackgrounds) {
			if (el.dataset.bg) {
				el.style.backgroundImage = `url(${el.dataset.bg})`;
				el.removeAttribute('data-bg');
			}
		}
	}

	disconnectedCallback() {
		this.disconnectBootObservers();
		if (this.splide) {
			this.splide.destroy();
		}
		if (this.resizeTimeout) {
			clearTimeout(this.resizeTimeout);
			this.resizeTimeout = null; // Clear reference to prevent execution
		}
		if (this.modalObserver) {
			this.modalObserver.disconnect();
			this.modalObserver = null;
		}
		if (this.imageObserver) {
			this.imageObserver.disconnect();
			this.imageObserver = null;
		}
		window.removeEventListener('resize', this.handleResize, { passive: true });
	}

	handleResize() {
		// Debounce resize handler for better performance
		if (this.resizeTimeout) {
			clearTimeout(this.resizeTimeout);
		}
		
		this.resizeTimeout = setTimeout(() => {
			// Check if element is still connected before executing callback
			if (!this.isConnected) {
				return;
			}
			
			const enableDesktopCarousel = this.getAttribute('enable-desktop-carousel') === 'true';
			const enableMobileCarousel = this.getAttribute('enable-mobile-carousel') === 'true';
			const isMobile = window.innerWidth < 750;

			// Determine if we need to reinitialize
			const shouldBeCarousel = (isMobile && enableMobileCarousel) || (!isMobile && enableDesktopCarousel);
			const isCurrentlyCarousel = this.splide !== null;

			if (shouldBeCarousel !== isCurrentlyCarousel) {
				// Reload the page to reinitialize
				window.location.reload();
			}
		}, 250);
	}

	initSplide(deviceSpecificOptions = {}) {
		// Base options
		const options = {
			type: 'slide',
			arrows: true,
			pagination: true,
			autoplay: false,
			interval: 5000,
			speed: 400,
			perMove: 1,
			...deviceSpecificOptions,
		};

		// is-rendered BEFORE .splide so Splide's visibility:hidden does not delay LCP
		this.classList.add('is-rendered', 'splide');
		this.splide = new Splide(this, options);

		this.setupIntersectionObserver();

		this.splide.on('mounted', () => {
			this.loadDeferredImages();
			this.fixQuickBuyModals();
		});

		this.splide.on('move', () => {
			this.loadDeferredImages();
		});

		if (options.autoplay) {
			this.splide.on('autoplay:playing', () => {
				this.preloadNextSlideImage();
			});
		}

		this.splide.mount();
	}
	
	setupIntersectionObserver() {
		// Only load images when slides are near viewport
		if ('IntersectionObserver' in window) {
			const imageObserver = new IntersectionObserver((entries) => {
				entries.forEach(entry => {
					if (entry.isIntersecting) {
						const slide = entry.target;
						// Only load images if slide is visible and within 2 slides of active
						if (this.splide) {
							const slides = Array.from(this.querySelectorAll('.splide__slide'));
							const slideIndex = slides.indexOf(slide);
							const activeIndex = this.splide.index;
							
							// Load if within 2 slides of active
							if (Math.abs(slideIndex - activeIndex) <= 2) {
								this.loadSlideImages(slide);
								imageObserver.unobserve(slide);
							}
						}
					}
				});
			}, {
				rootMargin: '50px' // Start loading 50px before slide enters viewport
			});
			
			// Observe all slides
			const slides = this.querySelectorAll('.splide__slide');
			slides.forEach(slide => {
				imageObserver.observe(slide);
			});
			
			// Store observer for cleanup
			this.imageObserver = imageObserver;
		}
	}

	loadDeferredImages() {
		if (!this.splide) return;
		
		const activeIndex = this.splide.index;
		const slides = this.querySelectorAll('.splide__slide');
		
		// Use requestAnimationFrame for better performance
		requestAnimationFrame(() => {
			for (let i = 0; i < slides.length; i++) {
				// Load current slide and adjacent slides only (distance <= 1)
				// This prevents loading all images at once
				if (Math.abs(i - activeIndex) <= 1) {
					this.loadSlideImages(slides[i]);
				} else {
					// Ensure inactive slides have lazy loading
					const images = slides[i].querySelectorAll('img:not([loading])');
					images.forEach(img => {
						if (!img.src || img.src === 'data:,') {
							img.loading = 'lazy';
						}
					});
				}
			}
		});
	}

	preloadNextSlideImage() {
		if (!this.splide) return;
		
		const nextIndex = (this.splide.index + 1) % this.splide.length;
		const nextSlide = this.querySelectorAll('.splide__slide')[nextIndex];
		
		if (nextSlide) {
			this.loadSlideImages(nextSlide);
		}
	}

	loadSlideImages(slide) {
		const deferredImages = slide.querySelectorAll('.deferred-image');
		const isMobile = window.innerWidth < 750;
		
		for (const img of deferredImages) {
			if (!img.src && img.dataset.desktopSrc) {
				const srcToUse = isMobile ? 
					(img.dataset.mobileSrc || img.dataset.desktopSrc) : 
					img.dataset.desktopSrc;
				
				// Use native lazy loading if not already set
				if (!img.hasAttribute('loading')) {
					img.loading = 'lazy';
				}
				
				img.src = srcToUse;
				img.style.opacity = '1';
				img.classList.remove('deferred-image');
				
				// Remove data attributes to prevent reloading
				delete img.dataset.desktopSrc;
				delete img.dataset.mobileSrc;
			}
		}
		
		// Also handle existing lazy loading
		this.lazyLoadImagesInElement(slide);
	}

	lazyLoadInactiveSlides() {
		if (!this.splide) return;
		
		const slides = this.querySelectorAll('.splide__slide');
		const activeIndex = this.splide.index;
		
		slides.forEach((slide, index) => {
			if (Math.abs(index - activeIndex) <= 1) { // Only load adjacent slides (distance 0 or 1)
				this.lazyLoadImagesInElement(slide);
			}
		});
	}

	fixQuickBuyModals() {
		// Get all slides
		const slides = this.querySelectorAll('.splide__slide');
		
		// Process each slide
		slides.forEach(slide => {
			// Get the slide ID (e.g., "splide04-slide01")
			const slideId = slide.id;
			
			// Find quick buy button and modal in this slide
			const quickBuyButton = slide.querySelector('.quick-buy-button');
			const quickBuyModal = slide.querySelector('.quick-buy-modal');
			
			if (quickBuyButton && quickBuyModal) {
				// Get the original IDs
				const originalModalId = quickBuyModal.id;
				
				// Create a new unique ID using the slide ID
				const newModalId = `${originalModalId}-${slideId}`;
				
				// Update the modal ID
				quickBuyModal.id = newModalId;
				
				// Update the button's aria-controls attribute
				quickBuyButton.setAttribute('aria-controls', newModalId);

				// Reconnect modal so DialogElement rebinds its aria-controls click delegate
				const modalParent = quickBuyModal.parentNode;
				const modalNextSibling = quickBuyModal.nextSibling;
				quickBuyModal.remove();
				modalParent.insertBefore(quickBuyModal, modalNextSibling);
			}
		});
	}

	static reinitIn(root) {
		if (!root) return;
		root.querySelectorAll('custom-carousel').forEach((carousel) => {
			if (typeof carousel.reinit === 'function') {
				carousel.reinit();
			}
		});
	}

	static reinitializeAll() {
		const drawer = document.querySelector('cart-drawer');
		if (drawer) {
			CustomCarousel.reinitIn(drawer);
			return;
		}
		document.querySelectorAll('custom-carousel').forEach((carousel) => {
			if (typeof carousel.reinit === 'function') {
				carousel.reinit();
			}
		});
	}
}

// Register the custom element
customElements.define('custom-carousel', CustomCarousel);

(function bindCartCarouselRescue() {
	const CART_SELECTORS =
		'[aria-controls="cart-drawer"], .header__cart-link, .bottom-cart-icon, bottom-cart, .bottom-cart';

	const reinitCart = () => {
		const drawer = document.querySelector('cart-drawer');
		if (!drawer) return;
		CustomCarousel.reinitIn(drawer);
	};

	document.addEventListener(
		'click',
		(event) => {
			if (event.target.closest && event.target.closest(CART_SELECTORS)) {
				requestAnimationFrame(reinitCart);
				setTimeout(reinitCart, 80);
				setTimeout(reinitCart, 320);
			}
		},
		true
	);

	document.addEventListener('dialog:after-show', (event) => {
		if (event.target && event.target.matches && event.target.matches('cart-drawer')) {
			reinitCart();
		}
	});

	document.addEventListener('cart-drawer:refreshed', () => {
		const drawer = document.querySelector('cart-drawer');
		if (drawer && drawer.hasAttribute('open')) {
			setTimeout(reinitCart, 50);
			setTimeout(reinitCart, 280);
		}
	});
})();

class ProductListTabs extends HTMLElement {
	constructor() {
		super();
		this.tabs = [];
		this.tabContents = [];
		this.transitionDelay = 150;
		this.initializedTabs = new Set();
		this.modalInitialized = false;
	}

	handleTimeBoundTabs() {
		const now = new Date();
		const tabLinks = this.querySelectorAll('.custom-tab-link');
		
		tabLinks.forEach(tab => {
			const enableTimeBound = tab.getAttribute('data-enable-time-bound') === 'true';
			if (!enableTimeBound) return;
			
			const startTimeStr = tab.getAttribute('data-start-time');
			const endTimeStr = tab.getAttribute('data-end-time');
			const tabId = tab.getAttribute('data-tab');
			const content = this.querySelector(`.custom-tab-content[data-tab="${tabId}"]`);
			
			let isVisible = true;
			
			if (startTimeStr) {
				// Handle "YYYY-MM-DD HH:mm" format by replacing space with T for ISO compatibility
				const startDate = new Date(startTimeStr.replace(' ', 'T'));
				if (!isNaN(startDate.getTime()) && now < startDate) {
					isVisible = false;
				}
			}
			
			if (isVisible && endTimeStr) {
				const endDate = new Date(endTimeStr.replace(' ', 'T'));
				if (!isNaN(endDate.getTime()) && now > endDate) {
					isVisible = false;
				}
			}
			
			if (!isVisible) {
				tab.remove();
				if (content) content.remove();
			}
		});
	}

	connectedCallback() {
		this.tabsContainer = this.querySelector('.custom-tabs');
		this.contentContainer = this.querySelector('.product-tabs-content');
		this.handleTimeBoundTabs();
		this.tabs = Array.from(this.querySelectorAll('.custom-tab-link'));
		this.tabContents = Array.from(this.querySelectorAll('.custom-tab-content'));

		const isInModal = this.closest('.modal') !== null;
		if (isInModal) {
			this.setupModalTabsLater();
			this.bindTabClicks();
			if (this.tabs.length > 0) {
				this.activateTab(this.tabs[0], true);
			}
			return;
		}

		if (window.Shopify && Shopify.designMode) {
			this.initTabs();
			return;
		}

		this.initTabs();
	}

	initTabs() {
		if (this._tabsReady) return;
		this._tabsReady = true;
		this.setupTabs();
		if (this.tabs.length > 0) {
			this.activateTab(this.tabs[0], true);
		}
		this.bindTabClicks();
	}

	bindTabClicks() {
		if (this._tabsBound) return;
		this._tabsBound = true;
		this.tabs.forEach((tab) => {
			tab.addEventListener('click', () => {
				this.activateTab(tab);
			});
		});
	}
	
	setupModalTabsLater() {
		// Find the modal element
		const modal = this.closest('.modal');
		if (!modal) return;
		
		// Store observer reference so we can reconnect if needed
		if (!this.modalObserver) {
			// Wait for modal to be shown
			this.modalObserver = new MutationObserver((mutations) => {
				mutations.forEach((mutation) => {
					if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
						if (modal.hasAttribute('open') && !this.modalInitialized) {
							// Modal is now open, initialize tabs
							setTimeout(() => {
								this.setupTabs();
								this.modalInitialized = true;
							}, 100); // Small delay to ensure content is rendered
						} else if (!modal.hasAttribute('open') && this.modalInitialized) {
							// Modal closed, reset initialization flag so tabs can reinitialize on next open
							this.modalInitialized = false;
						}
					}
				});
			});
			
			this.modalObserver.observe(modal, { attributes: true });
		}
		
		// Fallback: if modal is already open when this runs
		if (modal.hasAttribute('open') && !this.modalInitialized) {
			setTimeout(() => {
				this.setupTabs();
				this.modalInitialized = true;
			}, 100);
		}
	}
	
	setupTabs() {
		// Modals must keep every collection's markup. Clearing non-active
		// grids leaves <ul class="product-grid"> empty, and tab switches
		// then have nothing to show.
		const preserveContent = this.closest('.modal, x-modal') !== null;

		this.tabContents.forEach((content, index) => {
			if (index !== 0 && !preserveContent) {
				const productGrid = content.querySelector('.product-grid');
				if (productGrid) {
					content.dataset.originalGridContent = productGrid.innerHTML;
					productGrid.innerHTML = '';
				} else {
					content.dataset.originalGridContent = content.innerHTML;
					content.innerHTML = '';
				}
			}
			if (index !== 0) content.style.display = 'none';
			content.style.opacity = index === 0 ? '1' : '0';
		});
	}
	
	activateTab(selectedTab, isInitialLoad = false) {
		// Don't do anything if this tab is already active
		if (selectedTab.classList.contains('current')) {
			return;
		}
		
		// Update tab states
		this.tabs.forEach(tab => {
			tab.classList.toggle('current', tab === selectedTab);
		});
		
		// Get the currently active content
		const currentContent = this.querySelector('.custom-tab-content.current');
		
		// Get the new content to show
		const tabId = selectedTab.getAttribute('data-tab');
		const newContent = this.querySelector(`.custom-tab-content[data-tab="${tabId}"]`);
		
		if (currentContent) {
			// Fade out current content
			currentContent.style.opacity = '0';
			
			// After transition, hide current and show new
			setTimeout(() => {
				currentContent.classList.remove('current');
				currentContent.style.display = 'none';
				
				this.loadTabContent(newContent);
				newContent.classList.add('current');
				newContent.style.display = 'block';
				
				// Trigger reflow to ensure transition happens
				void newContent.offsetWidth;
				
				// Fade in new content
				newContent.style.opacity = '1';
				
				// Reset the grid view for the new tab
				this.resetGridView(newContent);
				
				// Reinitialize carousels in the newly visible tab
				if (window.CustomCarousel) {
					setTimeout(() => {
						CustomCarousel.reinitializeAll();
						// Dispatch Swym event after carousels are reinitialized
						document.dispatchEvent(new CustomEvent("swym:collections-loaded"));
					}, 100);
				} else {
					// Dispatch Swym event immediately if no carousel reinitialization needed
					document.dispatchEvent(new CustomEvent("swym:collections-loaded"));
				}
			}, this.transitionDelay);
		} else {
			// No current content, just show the new one
			this.loadTabContent(newContent);
			newContent.classList.add('current');
			newContent.style.display = 'block';
			newContent.style.opacity = '1';
			
			// Reset the grid view for the new tab
			this.resetGridView(newContent);
			
			// Reinitialize carousels
			if (window.CustomCarousel) {
				setTimeout(() => {
					CustomCarousel.reinitializeAll();
					// Dispatch Swym event after carousels are reinitialized
					document.dispatchEvent(new CustomEvent("swym:collections-loaded"));
				}, 100);
			} else {
				// Dispatch Swym event immediately if no carousel reinitialization needed
				document.dispatchEvent(new CustomEvent("swym:collections-loaded"));
			}
		}
	}
	
	loadTabContent(tabContent) {
		// Only load content if it hasn't been loaded before and isn't the first tab
		if (!this.initializedTabs.has(tabContent) && tabContent !== this.tabContents[0]) {
			// Check if original content was stored
			if (typeof tabContent.dataset.originalGridContent !== 'undefined') {
				// Find the product grid and only restore that part
				const productGrid = tabContent.querySelector('.product-grid');
				if (productGrid) {
					// Restore only the grid content
					productGrid.innerHTML = tabContent.dataset.originalGridContent;
				} else {
					// If no product grid exists, restore entire content as fallback
					tabContent.innerHTML = tabContent.dataset.originalGridContent;
				}
			}
			
			// Initialize any carousels in this content
			const carousels = tabContent.querySelectorAll('custom-carousel');
			carousels.forEach(carousel => {
				if (carousel.connectedCallback) {
					carousel.connectedCallback();
				}
			});
			
			// Mark this tab as initialized
			this.initializedTabs.add(tabContent);
		}
	}
	
	resetGridView(tabContent) {
		const carousel = tabContent.querySelector('custom-carousel');
		if (!carousel || carousel.splide) return;

		const productGrid = carousel.querySelector('.product-grid, .image-link-grid, .splide__list, ul');
		if (!productGrid || productGrid.children.length === 0) return;

		const isMobile = window.innerWidth < 750;
		carousel.list = productGrid;
		carousel.columnsPerRow = isMobile
			? (parseInt(carousel.getAttribute('mobile-columns')) || 2)
			: (parseInt(carousel.getAttribute('desktop-columns')) || 3);
		carousel.totalRows = Math.ceil(productGrid.children.length / carousel.columnsPerRow);
		carousel.currentlyShownRows = carousel.initialRowsToShow || 2;
		carousel.viewAllContainer = tabContent.querySelector('.product-tabs-content-bottom');
		if (carousel.enableViewMore === undefined) {
			carousel.enableViewMore = carousel.getAttribute('enable-view-more') !== 'false';
		}

		if (!carousel.enableViewMore || carousel.totalRows <= carousel.currentlyShownRows) {
			carousel.currentlyShownRows = carousel.totalRows;
			carousel.applyViewMoreVisibility();
			tabContent.querySelector('.grid-view-buttons')?.remove();
			if (carousel.viewAllContainer) carousel.viewAllContainer.style.display = 'flex';
			return;
		}

		carousel.applyViewMoreVisibility();

		let buttonContainer = tabContent.querySelector('.grid-view-buttons');
		if (!buttonContainer) {
			buttonContainer = document.createElement('div');
			buttonContainer.className = 'grid-view-buttons';
			productGrid.parentNode.insertBefore(buttonContainer, productGrid.nextSibling);
		}
		carousel.createViewMoreButton(buttonContainer);

		if (carousel.viewAllContainer) carousel.viewAllContainer.style.display = 'none';
	}
}

customElements.define('product-list-tabs', ProductListTabs);